import { useMemo, useCallback } from "react";
import { getCookie } from "@/utils/cookies";
import { useGetSlotsQuery } from "@/queries/useGetSlotsQuery";
import { useGetAppointmentsQuery } from "@/queries/useGetAppointmentsQuery";
import { useGetClubsQuery } from "@/queries/useGetClubsQuery";
import type { Slot } from "@/utils/booking";

export const useSlotProcessing = (idClub: string | null) => {
  const userToken = getCookie("user_token");

  const { data: slotsData, isLoading: slotsLoading } = useGetSlotsQuery(idClub, userToken);
  const { data: appointmentsData } = useGetAppointmentsQuery({
    activeOnly: true,
  });
  const { data: clubsListData } = useGetClubsQuery();

  const appointmentClubById = useMemo(() => {
    const map = new Map<string, string>();
    if (!appointmentsData || !clubsListData) return map;

    const clubsById = new Map<string, string>(
      clubsListData.map((club: { id: string; title: string }) => [club.id, club.title])
    );

    appointmentsData.forEach((appointment) => {
      if (appointment.appointment_id === undefined) return;
      const clubTitle = clubsById.get(appointment.club_id);
      if (clubTitle) {
        map.set(String(appointment.appointment_id), clubTitle);
      }
    });

    return map;
  }, [appointmentsData, clubsListData]);

  const { slots, slotDays } = useMemo(() => {
    if (!slotsData || !appointmentsData || !clubsListData) {
      return { slots: [] as Slot[], slotDays: [] as string[] };
    }

    const slotDaysSet = new Set<string>();
    const slotsArray: Slot[] = Object.values(slotsData);

    const processedSlots = slotsArray.map((slot) => {
      if (slot.date_time) {
        const slotDate = slot.date_time.substring(0, 10);
        slotDaysSet.add(slotDate);

        if (slot.appointment_id) {
          const clubTitle = appointmentClubById.get(String(slot.appointment_id));
          if (clubTitle) {
            return { ...slot, club_title: clubTitle };
          }
        }
      }
      return slot;
    });

    return {
      slots: processedSlots,
      slotDays: Array.from(slotDaysSet),
    };
  }, [slotsData, appointmentClubById]);

  const hasBookingOnDay = useCallback(
    (date: string) => slots.some((slot) => slot.date_time.startsWith(date) && slot.appointment_id),
    [slots]
  );

  // Get booking club name for a day if it differs from selected club
  const getBookingClubOnDay = useCallback(
    (date: string, selectedClubName: string): string | null => {
      const bookedSlot = slots.find(
        (slot) => slot.date_time.startsWith(date) && slot.appointment_id && slot.club_title
      );
      if (bookedSlot?.club_title && bookedSlot.club_title !== selectedClubName) {
        return bookedSlot.club_title;
      }
      return null;
    },
    [slots]
  );

  return {
    slots,
    slotDays,
    slotsLoading,
    hasBookingOnDay,
    getBookingClubOnDay,
    appointmentsData,
  };
};
